import { YoutubeProcessor } from '../models/media/youtube.processor';
import type { GoMediaPlayer } from './adapters/media-player';
import { YoutubePlayerAdapter } from './adapters/youtube-adapter';
import type { OpenTokSessionFactory } from '../models/opentok-session';
import { OpenTokRole } from '../models/opentok-session';
import type { PlaylistItem } from './playlist-item';
import type { Options } from './options';
import { MediaSource } from 'go-modules/models/media';
import type { MediaSchema } from 'go-modules/models/media';
import { STATES, EVENTS } from '.';
import { UserService } from 'go-modules/models/user/user.service';
import { AllPlayersService } from '@go-modules/services/all-players/all-players.service';
import { HTML5PlayerAdapter } from './adapters/html5-player-adapter';
import type { HTML5PlayerOptions, HTMLTrackOptions } from './adapters/html5-player-adapter';
import {
	ApiGetMediaRecordingStats,
	NgxMediaRecordingStatsService, RecordingConnectionQuality
} from '@go-modules/services/media-recording-stats/media-recording-stats.service';
import { catchError, EMPTY, Observable, shareReplay } from 'rxjs';
import { NgxMediaAnalyticsService } from '@go-modules/services/media/analytics/media-analytics.service';
import { NgxLoggerService } from '@go-modules/services/logger/logger.service';

interface PropertyBindings {
	id?: string;
	options?: Options;
	playlistItem: PlaylistItem;
	onInit: (args: {player: GoMediaPlayer}) => void;
	onInitError: (args: {error: Error}) => void;
}

const defaultOptions: Options = {
	liveStream: false,
	hideControls: false,
	autoPlay: false,
	forceCaptions: false,
	allowFullscreen: true,
	showSettings: true
};

let instanceCount = 0;

export class GoMediaPlayerController implements PropertyBindings {
	// Property bindings
	public audioStream: MediaStream;
	public playerState: STATES = STATES.IDLE;
	public mediaId: number = null;
	public id: string;
	public options: Options;
	public playlistItem: PlaylistItem;
	public onInit: (args: {player: GoMediaPlayer}) => void;
	public onInitError: (args: {error: Error}) => void;
	public scrubbing: boolean = false;
	public analyticsInstanceId: string;
	public collapseAudio: boolean = false;
	public minimizedControls: boolean = false;
	public allowDownload: boolean = false;

	public player: GoMediaPlayer;
	public playerOptions: Options;
	public setupPromise: ng.IPromise<void> = this.$q.resolve();
	private playerWrapper: HTMLElement;
	private shield: HTMLElement;
	private connectionCheckCache = new Map<string, Observable<ApiGetMediaRecordingStats[]>>();
	public showPoorConnectionIcon = false;

	/* @ngInject */
	constructor (
		private $element: ng.IAugmentedJQuery,
		private $scope: ng.IScope,
		private $q: ng.IQService,
		private OpenTokSession: OpenTokSessionFactory,
		private mediaPlayer: any,
		private ngxMediaAnalyticsService: NgxMediaAnalyticsService,
		private ngxMediaRecordingStatsService: NgxMediaRecordingStatsService,
		private userService: UserService,
		private allPlayersService: AllPlayersService
	) {
		this.playerWrapper = this.$element[0].querySelector('.player-wrapper');
	}

	public $onChanges (changes: ng.IOnChangesObject): void {
		if (changes.playlistItem) {
			this.setupPromise = this.setupPromise
				.then(() => this.destroyPlayer())
				.then(() => this.init(changes.playlistItem.currentValue));
		}
	}

	public init (newPlaylistItem): ng.IPromise<void> {
		// Reset values in case $onChanges triggers another player init
		this.showPoorConnectionIcon = false;

		// Ignore falsy playlist items
		if (!newPlaylistItem ) {
			return;
		}
		this.playerOptions = this.buildOptions(defaultOptions, this.options);

		if (this.collapseAudio) {
			this.playerOptions.allowFullscreen = false;
			this.playerOptions.showSettings = false;
		}

		return this.$q.when(this.initPlayerWith(newPlaylistItem, this.playerOptions)).then((player: GoMediaPlayer) => {
			this.player = player;

			// Sets media analytics on player create.
			if(newPlaylistItem.media?.media_id) {
				this.analyticsInstanceId = this.ngxMediaAnalyticsService.createInstance(
					newPlaylistItem.media.media_id,
					this.userService.currentUser.user_id
				);
			}

			this.player.on(EVENTS.AUDIO_STREAM, this.setAudioStream);
			this.player.on(EVENTS.PLAY, this.playHandler.bind(this));
			this.player.on(EVENTS.PAUSE, this.durationHandler.bind(this));
			this.player.on(EVENTS.REQUEST_SEEK, this.seekHandler.bind(this));
			this.player.on(EVENTS.SEEK, this.seekEndHandler.bind(this));
			this.player.on(EVENTS.START_SCRUB, this.startScrubHandler.bind(this));
			this.player.on(EVENTS.FINISHED_SCRUB, this.endScrubHandler.bind(this));
			this.player.on(EVENTS.COMPLETE, this.durationHandler.bind(this));

			this.handlePlayerInit(this.player);
		}).catch((error) => {
			NgxLoggerService.logError(error);
			this.onInitError({error});
		});
	}

	public $onDestroy (): void {
		this.setupPromise = this.setupPromise
			.then(this.destroyPlayer.bind(this));
		this.shield?.removeEventListener('click', this.onShieldClick);
	}

	public seekHandler (): void {
		if (this.scrubbing) {
			return;
		}
		if (this.player.isPlaying()) {
			this.ngxMediaAnalyticsService.setStop(this.analyticsInstanceId, this.player.getTime());
		}
	}

	public seekEndHandler (): void {
		if (this.scrubbing) {
			return;
		}
		if (this.player.isPlaying()) {
			this.ngxMediaAnalyticsService.setStart(this.analyticsInstanceId, this.player.getTime());
		}
	}

	public startScrubHandler (): void {
		this.scrubbing = true;
		if (this.player.isPlaying()) {
			this.ngxMediaAnalyticsService.setStop(this.analyticsInstanceId, this.player.getTime());
		}
	}

	public endScrubHandler (): void {
		this.scrubbing = false;
		if (this.player.isPlaying()) {
			this.ngxMediaAnalyticsService.setStart(this.analyticsInstanceId, this.player.getTime());
		}
	}

	public playHandler (): void {
		this.ngxMediaAnalyticsService.incrementPlayCount(this.analyticsInstanceId);
		this.ngxMediaAnalyticsService.setStart(this.analyticsInstanceId, this.player.getTime());
		this.allPlayersService.setLastPlayed(this.player);
	}

	public durationHandler (): void {
		this.ngxMediaAnalyticsService.setStop(this.analyticsInstanceId, this.player.getTime());
	}

	public async initPlayerWith (playlistItem: PlaylistItem | MediaSchema, options: Options): Promise<GoMediaPlayer> {
		let url, thumbnailUrl;
		if (this.isPlaylistItem(playlistItem) && 'media' in playlistItem) {
			this.mediaId = playlistItem.media.media_id as number;
			url = playlistItem.media.media_url;
			thumbnailUrl = playlistItem.media.thumbnail_url;
		} else if (this.isPlaylistItem(playlistItem) && !('media' in playlistItem)) {
			this.mediaId = null;
			url = playlistItem.file;
			thumbnailUrl = playlistItem.image;
		} else if ('media_id' in playlistItem) {
			this.mediaId = playlistItem.media_id as number;
			url = playlistItem.media_url;
			thumbnailUrl = playlistItem.thumbnail_url;
		}
		const element = document.createElement('div');
		this.playerWrapper.innerHTML = '';
		this.playerWrapper.appendChild(element);
		this.initShield(this.playerWrapper);

		if (YoutubeProcessor.isYoutubeUrl(url)) {
			element.id = `youtube-adapter-iframe-${++instanceCount}`;
			element.dataset.cy = 'youtube-iframe';		// Custom unique selector for Cypress testing

			return YoutubePlayerAdapter.setup(element.id, {
				height: '100%',
				width: '100%',
				url
			});
		} else if (options.liveStream && 'media' in playlistItem && playlistItem.media?.source === MediaSource.OPENTOK) {
			element.className = 'opentok-session-adapter-container';

			const openTokAdapterModulePromise = import(/* webpackChunkName: "OpenTokStreamSubscriberAdapter" */ './adapters/opentok-adapter');
			const opentokSession = await this.OpenTokSession.getForMedia({
				media_id: playlistItem.media.media_id
			}).$promise;
			const opentokToken = await this.OpenTokSession.createToken({
				role: OpenTokRole.SUBSCRIBER,
				session_id: opentokSession.id
			}).$promise;

			const openTokAdapterModule = await openTokAdapterModulePromise;
			return openTokAdapterModule.OpenTokStreamSubscriberAdapter.setup({
				api_key: opentokToken.api_key,
				session_id: opentokSession.id,
				token: opentokToken.value,
				hasUsedScreenCapture: opentokSession.hasUsedScreenCapture,
				insertMode: 'append',
				height: '100%',
				width: '100%',
				showControls: false,
				fitMode: 'contain',
				subscribeToAudio: true,
				subscribeToVideo: true,
				containerElement: element
			});
		} else if (url == null) {
			throw new Error('Could not determine what to do with media');
		} else {
			element.className = 'html5-player-adapter-container';
			const html5options: HTML5PlayerOptions = {
				url,
				thumbnailUrl,
				tracks: []
			};
			if (this.isPlaylistItem(playlistItem)) {
				html5options.tracks = playlistItem.tracks.map((vtt, index): HTMLTrackOptions => ({
					default: index === 0,
					kind: 'captions',
					label: 'Caption ' + index,
					src: vtt.file
				}));
			}

			const playerPromise = HTML5PlayerAdapter.setup(element, html5options);

			if ('media' in playlistItem) {
				playerPromise.then((player) => player.on(EVENTS.TIME, this.checkConnection.bind(this, playlistItem)));
			}

			return playerPromise;
		}
	}

	private checkConnection (playlistItem, { time }): void {
		const mediaId = playlistItem.media.media_id;

		if (!this.connectionCheckCache.has(mediaId)) {
			const statsRequest$ = this.ngxMediaRecordingStatsService.getMediaRecordingStats(mediaId).pipe(
				catchError(() => EMPTY),
				shareReplay(1)
			);

			this.connectionCheckCache.set(mediaId, statsRequest$);
		}

		this.connectionCheckCache.get(mediaId).subscribe((stats) => {
			this.showPoorConnectionIcon = stats.some((stat) => {
				return stat.connection_quality === RecordingConnectionQuality.POOR
					&& stat.record_start_ms <= time && time <= stat.record_end_ms;
			});
		});
	}

	public setAudioStream = (stream: MediaStream | null) => {
		this.audioStream = stream?.clone();
		this.$scope.$evalAsync();
	};

	public setPlayerState = ({newstate}) => {
		this.playerState = newstate;
		this.$scope.$evalAsync();
	};

	private isPlaylistItem (playlistItem: PlaylistItem | MediaSchema): playlistItem is PlaylistItem {
		return 'media' in playlistItem || 'file' in playlistItem;
	}

	private buildOptions (defaults: Options, options: Options): Options {
		// TODO: the `live-view/view.controller` updates the `liveStreamCount` option
		// and the player controls is updating the UI when that value changes. The live
		// viewer count should really be managed by the media player instance and this controller.
		// TODO: The widgets refer to trimActive to know when to send trim points
		if (options && (options.liveStream || options.trimActive != null)) {
			return options;
		}

		return {
			...defaults,
			...options
		};
	}

	/**
	 * The shield prevents users from clicking on the display to toggle play/pause
	 */
	private initShield (container: HTMLElement): void {
		this.shield = document.createElement('div');
		this.shield.className = 'shield';
		container.append(this.shield);
		this.shield.addEventListener('click', this.onShieldClick);
	}

	private handlePlayerInit (player: GoMediaPlayer): void {
		if (this.id) {
			this.mediaPlayer.register(this.id, player);
		}
		this.onInit({player});
		this.playerEvents();
	}

	private playerEvents = () => {
		this.player.on(EVENTS.BUFFER, this.setPlayerState);
		this.player.on(EVENTS.PAUSE, this.setPlayerState);
		this.player.on(EVENTS.PLAY, this.setPlayerState);
		this.player.on(EVENTS.COMPLETE, this.setPlayerState);
	};

	private stopPlayerEvents () {
		this.player.off(EVENTS.BUFFER, this.setPlayerState);
		this.player.off(EVENTS.PAUSE, this.setPlayerState);
		this.player.off(EVENTS.PLAY, this.setPlayerState);
		this.player.off(EVENTS.COMPLETE, this.setPlayerState);
		this.player.off(EVENTS.AUDIO_STREAM, this.setAudioStream);
	}

	private destroyPlayer (): void {
		if (this.player) {
			this.stopPlayerEvents();

			// duration is only set on pause or complete so if still playing
			// then we need to set it
			if (this.player.isPlaying()) {
				this.ngxMediaAnalyticsService.setStop(this.analyticsInstanceId, this.player.getTime());
			}
			this.ngxMediaAnalyticsService.saveInstance(this.analyticsInstanceId);
			this.ngxMediaAnalyticsService.removeInstance(this.analyticsInstanceId);

			if (this.id) {
				this.mediaPlayer.unregister(this.id);
			}
			this.player.destroy();
			this.player = null;
		}
	}

	private onShieldClick = () => {
		if (this.options.disableControlsForSingleRecording && this.player.isComplete()) {
			return;
		}
		if (this.player.isPlaying()) {
			this.player.pause();
		} else {
			this.player.play();
		}
	};
}
